import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { scrollWindowToTop } from '../utils/scroll';
import { config } from '../config/runtimeConfig';

/**
 * ScrollToTop — сбрасывает scroll окна наверх при СМЕНЕ маршрута.
 *
 * React Router v6 НЕ восстанавливает/сбрасывает scroll position при навигации.
 * Поэтому при открытии VDP (`/cars/:id`) из прокрученного вниз каталога окно
 * оставалось на прежней позиции — страница детали открывалась «снизу», а не
 * с верха (bug: VDP focus at bottom).
 *
 * Реагируем ТОЛЬКО на смену `pathname`, не на `search` — иначе смена фильтров
 * каталога (`?make=...`, которая меняет только query-string) дёргала бы scroll.
 *
 * ВАЖНО: скроллим только когда pathname РЕАЛЬНО сменился относительно
 * предыдущего, а не на первичном mount. В embed-режиме registry Catalog
 * монтируется асинхронно — bootstrap ждёт fetch widget-config + dynamic
 * `import()` (см. `bootstrap.jsx`), т.е. уже ПОСЛЕ того как пользователь мог
 * проскроллить host page. Скролл живёт на общем host `window`, поэтому
 * `scrollTo` на mount дёрнул бы весь host page наверх (или увёл бы от машины
 * при открытии по deep-link `…#/cars/:id`). Сравнение с prev-pathname через
 * ref делает пропуск mount устойчивым и к `React.StrictMode` (включён и в
 * `main.jsx`, и в `bootstrap.jsx`): StrictMode в dev делает setup→cleanup→setup
 * на mount, поэтому булев `isFirstRender` сбрасывался бы уже на втором setup и
 * скролл всё равно случился бы на mount. Сравнение prev===current — идемпотентно.
 *
 * Скроллим прямо в effect (без `requestAnimationFrame`): effect уже выполняется
 * после DOM-commit нового роута, а RAF в скрытой/фоновой вкладке тормозится и
 * сброс стал бы хрупким.
 *
 * Сам сброс — в `scrollWindowToTop()` (`utils/scroll.js`), через
 * `scrollingElement.scrollTop`, а НЕ `window.scrollTo()`: host-страницы клиентов
 * (Andys — MODX + jQuery scroll-плагины) переопределяют `window.scrollTo` в
 * no-op, из-за чего первый фикс #1166 молча не работал в embed (VDP открывалась
 * на позиции скролла каталога, «снизу»). Тот же helper зовёт и
 * `services/commandExecutor.js` для agent-навигации.
 */
export default function ScrollToTop() {
  const { pathname } = useLocation();
  const prevPathname = useRef(pathname);

  useEffect(() => {
    if (prevPathname.current === pathname) return;
    prevPathname.current = pathname;
    // Витрина: новая страница открывается сверху сразу, без проезда.
    scrollWindowToTop({ instant: config.IS_OWN_STOCK });
  }, [pathname]);

  return null;
}
